import AddCircleOutline from "@mui/icons-material/AddCircleOutline";
import CheckCircleOutlineOutlined from "@mui/icons-material/CheckCircleOutlineOutlined";
import Edit from "@mui/icons-material/Edit";
import { Box, Fab, IconButton, Stack, Typography } from "@mui/material";
import type { FC, ReactNode } from "react";
import { useState } from "react";
import { capitalizeEachWord } from "../../../utils/capitalize";
import type { UUID } from "../../../utils/uuid";
import type { BrandKitColor, BrandKitLogo } from "../server/brand-kit-service";

type BrandKitChoiceRowProps = {
  title?: string;
  subtitle?: string;
  onClick?: () => void;
  remove?: (index: number) => void;
  setSelected: (id?: UUID) => void;
  selectedText?: string;
  children?: ReactNode;
  showDefaultIndicator?: boolean;
  anchorRef?: React.RefObject<HTMLButtonElement | null>;
  addLabel?: string;
  onEdit?: (item: BrandKitLogo | BrandKitColor, index: number) => void;
} & (
  | {
      type: "logo";
      selected?: BrandKitLogo;
      existing: BrandKitLogo[];
    }
  | {
      type: "color";
      selected?: BrandKitColor;
      existing: BrandKitColor[];
    }
);

export const BrandKitChoiceRow: FC<BrandKitChoiceRowProps> = ({
  title,
  subtitle,
  type,
  existing,
  selected,
  onClick,
  remove,
  children,
  showDefaultIndicator = true,
  anchorRef,
  addLabel,
  onEdit,
  setSelected,
}) => {
  return (
    <Box>
      {title && <Typography variant="h6">{title}</Typography>}
      {subtitle && (
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      )}
      <Box sx={{ mt: (title || subtitle) && 4, display: "flex", gap: 2, flexWrap: "wrap" }}>
        {type === "logo"
          ? existing.map((value, index) => (
              <BrandKitBox
                key={index}
                selected={value.id === selected?.id}
                type="logo"
                remove={remove && (() => remove(index))}
                onEdit={onEdit && (() => onEdit(value, index))}
                setSelected={() =>
                  !onEdit && setSelected(value.id === selected?.id ? undefined : value.id)
                }
                showDefaultIndicator={showDefaultIndicator}
                content={
                  <img
                    src={value.src}
                    width="142px"
                    height="88px"
                    alt=""
                    style={{ objectFit: "contain" }}
                  />
                }
                label={
                  <>
                    <Typography variant="body2">
                      {capitalizeEachWord(value.type)}
                      {value.theme ? ` - ${capitalizeEachWord(value.theme)}` : ""}
                    </Typography>
                    {value.tag && (
                      <Typography variant="caption" color="text.secondary">
                        {value.tag}
                      </Typography>
                    )}
                  </>
                }
              />
            ))
          : existing.map((value, index) => (
              <BrandKitBox
                key={value.hex}
                selected={value.id === selected?.id}
                type="color"
                remove={remove && (() => remove(index))}
                onEdit={onEdit && (() => onEdit(value, index))}
                showDefaultIndicator={showDefaultIndicator}
                setSelected={() =>
                  !onEdit && setSelected(value.id === selected?.id ? undefined : value.id)
                }
                content={
                  <Box
                    data-testid="color-box"
                    sx={{
                      width: "100%",
                      minHeight: "88px",
                      flexGrow: 1,
                      bgcolor: value.hex,
                      borderRadius: "3px 3px 0 0",
                    }}
                  />
                }
                label={
                  <>
                    <Typography variant="body2">{value.hex}</Typography>
                    {value.tag && (
                      <Typography variant="caption" color="text.secondary">
                        {value.tag}
                      </Typography>
                    )}
                  </>
                }
              />
            ))}
        {onClick && (
          <Stack
            sx={{
              width: 146,
              height: 112,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              borderRadius: "4px",
              backgroundColor: "neutral.50",
              border: "1px solid",
              borderColor: "neutral.300",
            }}
          >
            <IconButton
              data-analytics-id={`brand-kit-add-${type}`}
              ref={anchorRef}
              onClick={onClick}
              type="button"
              data-testid={`add-${type}`}
            >
              <AddCircleOutline
                data-testid="AddCircleOutlineIcon"
                sx={{ fontSize: "32px" }}
                color="primary"
              />
            </IconButton>
            {addLabel && (
              <Typography variant="body2" fontWeight={400} color="text.primary">
                {addLabel}
              </Typography>
            )}
          </Stack>
        )}
        {children}
      </Box>
    </Box>
  );
};

const BrandKitBox: FC<{
  type: "color" | "logo" | "button";
  selected: boolean;
  remove?: () => void;
  onEdit?: () => void;
  content: ReactNode;
  label: ReactNode;
  width?: number;
  height?: number;
  showDefaultIndicator?: boolean;
  setSelected: () => void;
}> = ({
  type,
  selected,
  onEdit,
  content,
  label,
  width = 148,
  height = 108,
  setSelected,
  showDefaultIndicator,
}) => {
  const [hover, setHover] = useState(false);

  const title = selected ? "Default" : undefined;

  return (
    <Stack
      direction="column"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <Box
        onClick={setSelected}
        sx={{
          minWidth: width,
          minHeight: height,
          borderRadius: "4px",
          border: "1px solid",
          borderColor: "neutral.300",
          display: "flex",
          flexDirection: "column",
          backgroundColor: "neutral.100",
          cursor: "pointer",
          ":hover": {
            borderColor: "neutral.400",
          },
        }}
        title={title}
        data-analytics-id="branding-bar-logo"
      >
        <Box
          role="button"
          data-analytics-id={`brand-kit-content-${type}`}
          sx={{
            position: "relative",
            flexGrow: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: "4px 4px 0 0",
          }}
        >
          {content}
          {hover && onEdit && (
            <Fab
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: 1,
                borderRadius: "2px",
                width: "24px",
                height: "24px",
                minHeight: "24px",
              }}
            >
              <Edit sx={{ fontSize: "16px" }} />
            </Fab>
          )}
          {selected && showDefaultIndicator && (
            <Fab
              sx={(theme) => ({
                position: "absolute",
                top: "10px",
                left: "10px",
                zIndex: 1,
                width: "16px",
                height: "16px",
                minHeight: "16px",
                backgroundColor: theme.palette.primary.light,
                color: theme.palette.primary.contrastText,
                ":hover": {
                  backgroundColor: theme.palette.primary.main,
                },
              })}
            >
              <CheckCircleOutlineOutlined />
            </Fab>
          )}
        </Box>
        <Box
          sx={{
            backgroundColor: "background.paper",
            textAlign: "center",
            borderRadius: "0 0 3px 3px",
          }}
        >
          {label}
        </Box>
      </Box>
    </Stack>
  );
};
